"use client";

import { Dropdown } from "@money/frontend/components/ui/dropdown";
import Link from "next/link";
import { TrComponent } from "@money/frontend/components/leaderboard/tr.component.tsx";
import { Button } from "@money/frontend/components/ui/button";

interface Startup {
  id?: string;
  rank: number;
  name: string;
  slug: string;
  description: string;
  icon: string;
  iconBg: string;
  logoUrl?: string;
  founder: {
    name: string;
    avatar: string;
  };
  revenue: string;
  mrr: string;
}

interface LeaderboardTableProps {
  startups: Startup[];
  pagination: {
    page: number;
    totalPages: number;
    total: number;
    pageSize: number;
  };
}

function MedalIcon({ type, rank }: { type: "gold" | "silver" | "bronze"; rank: number }) {
  const colors = {
    gold: "#FFD700",
    silver: "#C0C0C0",
    bronze: "#CD7F32",
  };

  return (
    <svg
      width="35"
      height="35"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="text-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="rgba(0,0,0,0.8)" />
        </filter>
      </defs>
      <path
        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
        fill={colors[type]}
        stroke="white"
        strokeWidth="0.5"
      />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fill="white"
        fontSize="10"
        fontWeight="bold"
        filter="url(#text-shadow)"
        className="font-bold"
      >
        {rank}
      </text>
    </svg>
  );
}

function NumberIcon({ rank }: { rank: number }) {
  return (
    <svg
      width="35"
      height="35"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="text-shadow-number" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="rgba(0,0,0,0.8)" />
        </filter>
      </defs>
      <circle cx="12" cy="12" r="8" fill="rgba(0,0,0,0.4)" />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fill="white"
        fontSize="10"
        fontWeight="bold"
        filter="url(#text-shadow-number)"
        className="font-bold"
      >
        {rank}
      </text>
    </svg>
  );
}

function RankCell({ rank }: { rank: number }) {
  if (rank === 1) return <MedalIcon type="gold" rank={rank} />;
  if (rank === 2) return <MedalIcon type="silver" rank={rank} />;
  if (rank === 3) return <MedalIcon type="bronze" rank={rank} />;
  return <NumberIcon rank={rank} />;
}

function PaginationControls({ pagination }: { pagination: LeaderboardTableProps["pagination"] }) {
  const { page, totalPages } = pagination;
  
  const getPageUrl = (pageNum: number) => {
    if (pageNum === 1) {
      return "/";
    }
    return `/${pageNum}`;
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 mt-6 px-2">
      {page === 1 ? (
        <Button
          variant="outline"
          disabled
          className="px-2 sm:px-3 py-2 opacity-50 cursor-not-allowed text-xs sm:text-sm"
        >
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Prev</span>
        </Button>
      ) : (
        <Link href={getPageUrl(page - 1)}>
          <Button variant="outline" className="px-2 sm:px-3 py-2 text-xs sm:text-sm">
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </Button>
        </Link>
      )}
      
      <div className="flex items-center gap-1 flex-wrap justify-center">
        {getPageNumbers().map((pageNum, index) => {
          if (pageNum === "...") {
            return (
              <span key={`ellipsis-${index}`} className="px-1 sm:px-2 text-gray-500 text-xs sm:text-sm">
                ...
              </span>
            );
          }
          
          const isActive = pageNum === page;
          return (
            <Link key={pageNum} href={getPageUrl(pageNum as number)}>
              <Button
                variant={isActive ? "primary" : "outline"}
                className={`px-2 sm:px-3 py-2 min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm ${isActive ? "" : "hover:bg-gray-50"}`}
              >
                {pageNum}
              </Button>
            </Link>
          );
        })}
      </div>
      
      {page === totalPages ? (
        <Button
          variant="outline"
          disabled
          className="px-2 sm:px-3 py-2 opacity-50 cursor-not-allowed text-xs sm:text-sm"
        >
          Next
        </Button>
      ) : (
        <Link href={getPageUrl(page + 1)}>
          <Button variant="outline" className="px-2 sm:px-3 py-2 text-xs sm:text-sm">
            Next
          </Button>
        </Link>
      )}
    </div>
  );
}

export function LeaderboardTable({ startups, pagination }: LeaderboardTableProps) {
  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12">
      {/* Header with title and filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Leaderboard</h2>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <Dropdown options={["Revenue", "MRR", "Growth"]} />
          <Dropdown options={["All time", "This year", "This month"]} />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                  #
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                  Startup
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900 hidden md:table-cell">
                  Founder
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                  Revenue
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                  MRR
                </th>
              </tr>
            </thead>
            <tbody>
              {startups.map((startup) => (
                <TrComponent
                  link={`/company/${startup.slug}`}
                  key={startup.id || startup.rank}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center justify-start">
                      <RankCell rank={startup.rank} />
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {startup.logoUrl ? (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100 shrink-0">
                          <img
                            src={startup.logoUrl}
                            alt={startup.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-white font-semibold text-xs sm:text-sm shrink-0 ${startup.iconBg}`}
                        >
                          {startup.icon}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-gray-900 text-sm sm:text-base">
                          <Link href={`/company/${startup.slug}`} className="hover:underline">
                            {startup.name}
                          </Link>
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 mt-0.5 max-w-md truncate">
                          {startup.description}
                        </div>
                        <div className="md:hidden text-xs text-gray-500 mt-1">
                          {startup.founder.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 text-sm sm:text-base">
                        {startup.founder.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <span className="font-bold text-gray-900 text-sm sm:text-base whitespace-nowrap">
                      {startup.revenue}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <span className="text-gray-900 text-sm sm:text-base whitespace-nowrap">{startup.mrr}</span>
                  </td>
                </TrComponent>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <PaginationControls pagination={pagination} />
      )}
    </div>
  );
}
